import React, { useState } from 'react';
import Modal from '../../components/Modal';
import * as miniLeaguesApi from '../../api/miniLeagues';
import * as gamesApi from '../../api/games';

/**
 * The mirror of JoinGameModal. A code is six characters and gives nothing away
 * about what it opens, so this resolves it before joining anything.
 *
 * Two ways a league join can succeed. Get in before the league's first game
 * starts and you are simply in; turn up later and all you can do is ask, and
 * the manager decides. The second is not an error and must not read like one.
 *
 * A *game* code typed in here used to die as "Invalid league code". Now it says
 * what the code actually is and offers that game instead.
 */
function JoinLeagueModal({ onClose, onJoined }) {
  const [leagueCode, setLeagueCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requested, setRequested] = useState(null);
  const [pendingGame, setPendingGame] = useState(null);

  const failure = (err, fallback) => {
    if (err.name === 'ApiError') {
      setError(err.body?.error || fallback);
    } else {
      setError(fallback);
    }
  };

  const handleResolve = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const found = await gamesApi.resolveJoinCode(leagueCode);

      if (found.kind === 'UNKNOWN') {
        setError("That code doesn't match any mini-league or game.");
        return;
      }
      if (found.kind === 'GAME') {
        setPendingGame(found);
        return;
      }
      await joinTheLeague();
    } catch (err) {
      failure(err, 'Error checking that code');
    } finally {
      setSubmitting(false);
    }
  };

  // Not wrapped in its own submitting/try - handleResolve owns those when it
  // calls this, and joinFromPrompt owns them when the player confirms a game.
  const joinTheLeague = async () => {
    const result = await miniLeaguesApi.joinLeague(leagueCode);
    if (result?.outcome === 'REQUESTED') {
      setRequested(result);
    } else {
      onJoined();
    }
  };

  const joinTheGame = async () => {
    setError('');
    setSubmitting(true);
    try {
      await gamesApi.joinGame(leagueCode);
      onJoined();
    } catch (err) {
      failure(err, 'Failed to join game');
    } finally {
      setSubmitting(false);
    }
  };

  if (requested) {
    return (
      <Modal title="Request sent" onClose={onClose}>
        <div className="join-requested">
          <p>
            <strong>{requested.leagueName}</strong> has already started, so your
            request has gone to the manager.
          </p>
          <p className="helper-text">
            {requested.vacant
              ? "This league doesn't have a manager right now. Your request will be waiting for whoever takes it on."
              : "You'll be in as soon as they say yes. Nothing else to do."}
          </p>
          <button type="button" className="primary-btn full-width" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  if (pendingGame) {
    return (
      <Modal title="That's a game code" onClose={onClose}>
        <div className="join-choice">
          <p>
            <strong>{leagueCode}</strong> is the code for the game{' '}
            <strong>{pendingGame.gameName}</strong>, not a mini-league.
          </p>
          <p className="helper-text">
            {pendingGame.leagueName
              ? `That game is run by ${pendingGame.leagueName}, so joining it makes you a member of that mini-league too.`
              : 'That game is a standalone one, so joining it does not put you in a mini-league.'}
          </p>
          {error && <div className="error-message">{error}</div>}
          <button
            type="button"
            className="primary-btn full-width"
            onClick={joinTheGame}
            disabled={submitting}
          >
            {submitting ? 'Joining...' : `Join ${pendingGame.gameName}`}
          </button>
          <button
            type="button"
            className="secondary-btn full-width"
            onClick={() => setPendingGame(null)}
            disabled={submitting}
          >
            Use a different code
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Join a Mini League" onClose={onClose}>
      <form onSubmit={handleResolve}>
        <div className="form-group">
          <label>League Code</label>
          <input
            type="text"
            value={leagueCode}
            onChange={(e) => setLeagueCode(e.target.value.toUpperCase())}
            placeholder="Enter league code"
            maxLength={8}
            autoFocus
            required
            style={{ textTransform: 'uppercase', letterSpacing: '2px', textAlign: 'center', fontSize: '1.2rem' }}
          />
          <p className="helper-text">
            Joining a league enters you into every tournament it runs, now and in future.
            A game code works here too - we'll ask which you meant.
          </p>
        </div>
        {error && <div className="error-message">{error}</div>}
        <button type="submit" className="primary-btn full-width" disabled={submitting}>
          {submitting ? 'Joining...' : 'Join League'}
        </button>
      </form>
    </Modal>
  );
}

export default JoinLeagueModal;
